// tktuner sync — scan locally, then push NEW merged turns + events to the API.
//
// Incremental selection: raw sqlite rows with rowid > watermark identify the
// touched turnKeys; the WHOLE line group of each key is reloaded and re-merged
// (mergeTurnLines) so late-arriving content blocks of a message overwrite the
// server row via the (user_id, turn_key) upsert. The watermark only advances
// after a 2xx, so a crash re-sends at most one batch — idempotent by design.
//
// --quiet (used by the SessionEnd hook) NEVER fails: errors go to
// ~/.local/share/tktuner/sync.log and the exit code stays 0 so Claude Code is
// never disturbed.
import { appendFileSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import {
  SYNC_MAX_EVENTS_PER_BATCH,
  SYNC_MAX_TURNS_PER_BATCH,
  type SyncEventWire,
} from "@tktuner/core";
import pc from "picocolors";
import { apiCall } from "../lib/api-client";
import { readCredentials } from "../lib/credentials";
import { dataDir } from "../lib/paths";
import { toSyncEvent, toSyncTurn } from "../lib/privacy";
import { CLI_VERSION } from "../lib/version";
import { openDb } from "../store/db";
import { Store } from "../store/queries";
import { scan } from "./scan";

export interface SyncOptions {
  quiet?: boolean;
  full?: boolean;
  dryRun?: boolean;
  noScan?: boolean;
  db?: string;
  claudeDir?: string;
  api?: string;
}

export interface SyncOutcome {
  turnsPushed: number;
  eventsPushed: number;
  batches: number;
}

function logFailure(err: unknown): void {
  try {
    mkdirSync(dataDir(), { recursive: true });
    const line = `${new Date().toISOString()} sync failed: ${
      err instanceof Error ? err.message : String(err)
    }\n`;
    appendFileSync(join(dataDir(), "sync.log"), line);
  } catch {
    // even logging is best-effort in quiet mode
  }
}

export async function sync(opts: SyncOptions): Promise<SyncOutcome | null> {
  const say = (msg: string): void => {
    if (!opts.quiet) console.log(msg);
  };
  try {
    const creds = readCredentials();
    if (!creds) {
      if (opts.quiet) return null; // hook installed but not logged in — stay silent
      console.log(`Not logged in. Run ${pc.bold("tktuner login")} first.`);
      process.exitCode = 1;
      return null;
    }
    const apiUrl = opts.api ?? creds.apiUrl;

    if (!opts.noScan) {
      await scan({ db: opts.db, claudeDir: opts.claudeDir, silent: opts.quiet });
    }

    const db = openDb(opts.db);
    try {
      const store = new Store(db);

      // New destination (device/api changed) or --full => re-push everything.
      const boundDevice = store.getMeta("sync_device_id");
      const boundApi = store.getMeta("sync_api_url");
      if (opts.full || boundDevice !== creds.deviceId || boundApi !== apiUrl) {
        store.setMeta("sync_last_turn_rowid", "0");
        store.setMeta("sync_last_event_id", "0");
        store.setMeta("sync_device_id", creds.deviceId);
        store.setMeta("sync_api_url", apiUrl);
      }
      const turnWm = Number(store.getMeta("sync_last_turn_rowid") ?? "0");
      const eventWm = Number(store.getMeta("sync_last_event_id") ?? "0");

      const { keys } = store.newTurnKeysSince(turnWm);
      const groups = store.loadTurnGroups(keys);
      const pendingEvents = store.eventsSince(eventWm);

      if (opts.dryRun) {
        say(
          `${pc.bold("dry run")} — would push ${pc.green(String(groups.length))} turn(s) and ${pendingEvents.length} event(s) to ${apiUrl}`,
        );
        for (const g of groups.slice(0, 2)) {
          say(pc.dim(JSON.stringify(toSyncTurn(g.turn), null, 2)));
        }
        return { turnsPushed: 0, eventsPushed: 0, batches: 0 };
      }

      if (groups.length === 0 && pendingEvents.length === 0) {
        say(`${pc.bold("sync")} — already up to date (${apiUrl})`);
        return { turnsPushed: 0, eventsPushed: 0, batches: 0 };
      }

      const t0 = performance.now();
      let turnCursor = 0;
      let eventCursor = 0;
      let turnsPushed = 0;
      let eventsPushed = 0;
      let batches = 0;
      while (turnCursor < groups.length || eventCursor < pendingEvents.length) {
        const turnBatch = groups.slice(turnCursor, turnCursor + SYNC_MAX_TURNS_PER_BATCH);
        const eventBatch = pendingEvents.slice(
          eventCursor,
          eventCursor + SYNC_MAX_EVENTS_PER_BATCH,
        );
        const eventWires: SyncEventWire[] = eventBatch.map((e) => toSyncEvent(e.id, e.event));
        await apiCall("POST", apiUrl, "/v1/sync", {
          token: creds.deviceToken,
          gzip: true,
          body: {
            turns: turnBatch.map((g) => toSyncTurn(g.turn)),
            events: eventWires,
            cliVersion: CLI_VERSION,
          },
        });
        // 2xx confirmed — advance the watermarks past this batch.
        if (turnBatch.length > 0) {
          const maxRowid = turnBatch[turnBatch.length - 1]?.maxRowid ?? turnWm;
          store.setMeta("sync_last_turn_rowid", String(maxRowid));
        }
        if (eventBatch.length > 0) {
          const maxId = eventBatch[eventBatch.length - 1]?.id ?? eventWm;
          store.setMeta("sync_last_event_id", String(maxId));
        }
        turnCursor += turnBatch.length;
        eventCursor += eventBatch.length;
        turnsPushed += turnBatch.length;
        eventsPushed += eventWires.length;
        batches++;
      }
      const seconds = ((performance.now() - t0) / 1000).toFixed(1);
      say(
        `${pc.bold("sync complete")} — ${pc.green(String(turnsPushed))} turn(s), ${eventsPushed} event(s) in ${batches} batch(es), ${seconds}s → ${apiUrl}`,
      );
      return { turnsPushed, eventsPushed, batches };
    } finally {
      db.close();
    }
  } catch (err) {
    if (opts.quiet) {
      logFailure(err);
      return null; // exit 0 — never disturb the hook caller
    }
    console.error(pc.red(err instanceof Error ? err.message : String(err)));
    process.exitCode = 1;
    return null;
  }
}
